import { auth, db } from '../firebase';
import { collection, doc, getDocs, setDoc } from 'firebase/firestore';

const PREFIX = 'srs_';

function storageKey(langPair, wordKey) {
  return `${PREFIX}${langPair}_${wordKey}`;
}

/** SM-2 spaced-repetition algorithm. Grade 0–5 (0-2 = fail, 3-5 = pass). */
function applyGrade(card, grade) {
  if (grade < 3) {
    card.repetitions = 0;
    card.interval = 0;
  } else {
    const e = Math.max(1.3, card.easiness + 0.1 - (5 - grade) * (0.08 + (5 - grade) * 0.02));
    card.easiness = e;
    if (card.repetitions === 0)      card.interval = 1;
    else if (card.repetitions === 1) card.interval = 6;
    else card.interval = Math.min(365, Math.round(card.interval * card.easiness));
    card.repetitions++;
  }
  const next = new Date();
  if (grade >= 3) next.setDate(next.getDate() + card.interval);
  card.nextReview = next.toISOString();
  return card;
}

export const SrsService = {
  addCardIfNew(langPair, wordKey, nativeWord, targetWord, imageName = null, category = null) {
    const k = storageKey(langPair, wordKey);
    if (localStorage.getItem(k)) return;
    const card = {
      wordKey, nativeWord, targetWord, imageName, category, langPair,
      easiness: 2.5, interval: 1, repetitions: 0,
      nextReview: new Date().toISOString(),
    };
    localStorage.setItem(k, JSON.stringify(card));
  },

  getDueCards(langPair) {
    const prefix = `${PREFIX}${langPair}_`;
    const now = new Date();
    return Object.keys(localStorage)
      .filter(k => k.startsWith(prefix))
      .map(k => { try { return JSON.parse(localStorage.getItem(k)); } catch { return null; } })
      .filter(c => c && new Date(c.nextReview) <= now)
      .sort((a, b) => new Date(a.nextReview) - new Date(b.nextReview));
  },

  getDueCount(langPair) {
    return this.getDueCards(langPair).length;
  },

  async review(card, grade) {
    const k = storageKey(card.langPair, card.wordKey);
    let base = { ...card };
    try {
      const stored = JSON.parse(localStorage.getItem(k));
      if (stored) base = { ...stored, ...card };
    } catch {}

    const updated = applyGrade(base, grade);
    localStorage.setItem(k, JSON.stringify(updated));

    const uid = auth?.currentUser?.uid;
    if (uid && db) {
      setDoc(doc(db, 'users', uid, 'srs', `${card.langPair}_${card.wordKey}`), updated)
        .catch((e) => console.warn('SrsService: Firestore sync failed', e));
    }
    return updated;
  },

  /**
   * Pulls SRS cards from Firestore on login and fills in any that this
   * device doesn't already have locally. A fresh device (or cleared
   * localStorage) would otherwise have no due cards until each one is
   * reviewed again, and the next review would overwrite the cloud copy with
   * a reset one. Cards already present locally are left untouched — local
   * always wins for words this device has already reviewed, since there is
   * no last-modified timestamp to compare against.
   */
  async hydrateFromRemote(uid) {
    if (!uid || !db) return;
    try {
      const snapshot = await getDocs(collection(db, 'users', uid, 'srs'));
      snapshot.forEach((docSnap) => {
        const card = docSnap.data();
        const k = storageKey(card.langPair, card.wordKey);
        if (!localStorage.getItem(k)) {
          localStorage.setItem(k, JSON.stringify(card));
        }
      });
    } catch (e) {
      console.warn('SrsService: hydrateFromRemote failed', e);
    }
  },
};
